// Tiny client for /__app/me. Caches the response so repeated calls don't hit
// the middleware. Components import `useMe` to get the current user object, or
// `useMeState` to also observe the load status (loading / ok / error).
//
// Auth policy: if we cannot retrieve the current user for ANY reason — the
// session cookie expired (401), the server erred (5xx), the payload was empty,
// or the network threw — we treat the session as gone and funnel through
// forceLogout() (clears the cookie server-side, sends the user to login). We
// never render the app with an unknown user. `revalidateMe()` is the live
// re-check used by AuthWatcher; it is deliberately lenient on transient blips
// (it only logs out on a definitive 401) so a flaky connection doesn't kick an
// already-loaded session.
import { useEffect, useState } from 'react'
import { forceLogout } from './auth.js'
import { cachedGet } from './api.js'

let cache = null
let inflight = null
let status = 'loading' // 'loading' | 'ok' | 'error'

const listeners = new Set()
function emit() { for (const fn of listeners) fn() }
function fail() { status = 'error'; emit(); forceLogout(); return null }

async function fetchMe() {
  try {
    const r = await fetch('/__app/me', { credentials: 'include' })
    if (!r.ok) return fail()                    // 401 expired, or 5xx / any non-2xx
    const m = await r.json().catch(() => null)
    if (!m) return fail()                        // empty / unparseable → can't trust it
    // Training sandbox (train.smileseasons.com): mark the document BEFORE status
    // flips to 'ok', so the app — and every realtime subscription — mounts already
    // knowing. AuthGate paints the banner; realtime.js stays silent.
    if (m.training) document.documentElement.dataset.training = '1'
    cache = m
    status = 'ok'
    emit()
    return m
  } catch {
    return fail()                                // network error, offline, etc.
  }
}

export function getMe() {
  if (cache) return Promise.resolve(cache)
  if (inflight) return inflight
  inflight = fetchMe().then((m) => {
    inflight = null
    return m
  })
  return inflight
}

// Live re-validation (no cache). On an explicit 401 → force logout. On a network
// blip (offline, fetch threw) we keep the user where they are — we only log out
// on a definitive "session is gone" signal, never on a transient error.
export async function revalidateMe() {
  try {
    // ?live=1 → middleware also checks the Xano token is still valid, not just
    // the (longer-lived) session cookie.
    const r = await fetch('/__app/me?live=1', { credentials: 'include', cache: 'no-store' })
    if (r.status === 401) { forceLogout(); return null }
    if (!r.ok) return cache
    const m = await r.json().catch(() => null)
    if (m) cache = m
    return m
  } catch {
    return cache
  }
}

// Full auth state: the user object plus the load status. Layout/AuthGate use this
// to avoid rendering any page until the user is confirmed.
export function useMeState() {
  const [, forceRender] = useState(0)
  useEffect(() => {
    const rerender = () => forceRender((n) => n + 1)
    listeners.add(rerender)
    if (status === 'loading' && !inflight) getMe() // kick off the first fetch
    return () => { listeners.delete(rerender) }
  }, [])
  return { me: cache, status }
}

// Convenience: just the user object (or null). Existing callers use this.
export function useMe() {
  const [me, setMe] = useState(cache)
  useEffect(() => {
    if (cache) { setMe(cache); return }
    let alive = true
    getMe().then((m) => { if (alive) setMe(m) })
    return () => { alive = false }
  }, [])
  return me
}

// The name to SHOW for the current user, everywhere one is shown.
//
// /__app/me carries the Xano ACCOUNT label — whatever was typed at signup, which
// for plenty of staff is a latin transliteration ("ram bunphaphong"). The employee
// record is what HR maintains and what the clinic actually calls the person, so its
// Thai ชื่อ-นามสกุล wins whenever we have it, with the account name as the fallback
// (a doctor SSO session has no employee record at all, and /api/me/profile 403s).
//
// It lives here, next to useMe, because the sidebar card and the พื้นที่ของฉัน header
// showing two different names for the same person is exactly the bug this replaced.
// One memoized request via cachedGet; `empName` then caches the resolved string so a
// later mount renders the right name on the first paint instead of flashing the
// account name.
let empName = null // null = not fetched yet, '' = fetched, no employee name

export function useDisplayName() {
  const me = useMe()
  const [name, setName] = useState(empName || '')
  useEffect(() => {
    if (empName !== null) return   // already resolved (or known-empty) — no refetch
    let alive = true
    cachedGet('/api/me/profile')
      .then((r) => {
        empName = String(r?.name || '').trim()
        if (alive) setName(empName)
      })
      .catch(() => { empName = '' })   // no employee record / offline → account name
    return () => { alive = false }
  }, [])
  return name || me?.name || ''
}
